import React, { useState, useEffect } from 'react';
import { useNavigate, useSearchParams } from 'react-router-dom';
import confetti from 'canvas-confetti';
import { Ticket, Coins, Calendar, History, Crown } from 'lucide-react';
import { API_PATH, getToken, request } from '../config/api';
import { useAuth } from '../store/AuthContext';
import Button from '../components/ui/Button';
import CoinHistoryModal from '../components/CoinHistoryModal';
import CouponHistoryModal from '../components/CouponHistoryModal';
import PageHeroHeader from '../components/ui/PageHeroHeader';
import { formatDate } from '../utils/formatDate';
import styles from './SettingsPage.module.css';

const AMOUNT_PRESETS = [50, 100, 250, 500, 1000];
const MIN_AMOUNT = 10;
const MAX_AMOUNT = 100000;
// Fallbacks only, used before /payments/pricing responds — the real, enforced price is
// always superadmin-configured and read live from that endpoint; the server never trusts
// a client-submitted amount or tier.
const DEFAULT_COIN_BONUS = { threshold: 100, rate: 1.5 };
const DEFAULT_PLATINUM_TIERS = [{ months: 1, priceRupees: 2000 }];

const formatMonths = (months) => (months === 12 ? '1 year' : months === 36 ? '3 years' : `${months} month${months === 1 ? '' : 's'}`);

const CreditsCouponPage = () => {
    const navigate = useNavigate();
    const [searchParams, setSearchParams] = useSearchParams();
    const { user, refreshUser } = useAuth();

    const [couponCode, setCouponCode] = useState('');
    const [paymentStatus, setPaymentStatus] = useState(null); // 'completed' | 'pending' | 'failed' | 'unknown' | null
    const [paymentProduct, setPaymentProduct] = useState(null); // 'coins' | 'platinum' | null — which banner text to show
    const [returnOrderId, setReturnOrderId] = useState(null);
    const [redeemLoading, setRedeemLoading] = useState(false);
    const [redeemMessage, setRedeemMessage] = useState({ type: '', text: '' });
    const [coinHistoryOpen, setCoinHistoryOpen] = useState(false);
    const [couponHistoryOpen, setCouponHistoryOpen] = useState(false);
    const [buyAmount, setBuyAmount] = useState('');
    const [buyLoading, setBuyLoading] = useState(false);
    const [buyError, setBuyError] = useState('');
    const [platinumLoading, setPlatinumLoading] = useState(false);
    const [platinumError, setPlatinumError] = useState('');
    const [coinBonus, setCoinBonus] = useState(DEFAULT_COIN_BONUS);
    const [platinumTiers, setPlatinumTiers] = useState(DEFAULT_PLATINUM_TIERS);
    const [selectedMonths, setSelectedMonths] = useState(null);
    const [completedMonths, setCompletedMonths] = useState(null);

    useEffect(() => {
        let cancelled = false;
        (async () => {
            try {
                const res = await request(`${API_PATH}/payments/pricing`);
                const data = await res.json().catch(() => ({}));
                if (cancelled || !res.ok) return;
                if (data.coinBonus) setCoinBonus(data.coinBonus);
                if (Array.isArray(data.platinumTiers) && data.platinumTiers.length > 0) {
                    setPlatinumTiers(data.platinumTiers);
                    setSelectedMonths((prev) => prev ?? data.platinumTiers[0].months);
                }
            } catch {
                // keep fallback defaults — the button still works, just may show stale pricing
                // briefly; the server always re-validates the tier before charging anything.
            }
        })();
        return () => { cancelled = true; };
    }, []);

    const previewCoins = (() => {
        const amount = Number(buyAmount);
        if (!Number.isFinite(amount) || amount < MIN_AMOUNT || amount > MAX_AMOUNT) return null;
        const rate = amount >= coinBonus.threshold ? coinBonus.rate : 1;
        return Math.floor(amount * rate);
    })();
    const hasBonus = Number(buyAmount) >= coinBonus.threshold;
    const bonusPercentLabel = Math.round((coinBonus.rate - 1) * 100);

    const baseMonthlyRate = (() => {
        const oneMonth = platinumTiers.find((t) => t.months === 1);
        if (oneMonth) return oneMonth.priceRupees;
        const cheapest = platinumTiers.reduce((min, t) => {
            const perMonth = t.priceRupees / t.months;
            return min == null || perMonth < min ? perMonth : min;
        }, null);
        return cheapest || 0;
    })();
    const selectedTier = platinumTiers.find((t) => t.months === selectedMonths) || platinumTiers[0];

    useEffect(() => {
        const codeFromUrl = searchParams.get('code');
        if (codeFromUrl && codeFromUrl.trim()) {
            setCouponCode(codeFromUrl.trim());
            setSearchParams({}, { replace: true });
        }
    }, [searchParams, setSearchParams]);

    useEffect(() => {
        const isReturn = searchParams.get('payment') === 'return';
        const orderId = searchParams.get('order_id');
        if (!isReturn || !orderId) return;
        setSearchParams({}, { replace: true });
        setReturnOrderId(orderId);
        // eslint-disable-next-line react-hooks/exhaustive-deps
    }, []); // mount-only: read the return params exactly once, before they're stripped

    useEffect(() => {
        if (!returnOrderId) return;
        let cancelled = false;
        let timerId = null;

        const poll = async (attemptsLeft) => {
            try {
                const res = await request(`${API_PATH}/payments/status/${encodeURIComponent(returnOrderId)}`);
                const data = await res.json().catch(() => ({}));
                if (cancelled) return;
                if (res.status === 403 || res.status === 404) {
                    setPaymentStatus('unknown');
                    return;
                }
                if (res.ok && data.status === 'completed') {
                    setPaymentStatus('completed');
                    setPaymentProduct(data.product || 'coins');
                    setCompletedMonths(data.months || null);
                    refreshUser();
                    const fire = (opts) => confetti({ origin: { y: 0.75 }, ...opts });
                    fire({ particleCount: 80, spread: 55 });
                    setTimeout(() => fire({ particleCount: 60, spread: 100, startVelocity: 35 }), 150);
                    return;
                }
                if (res.ok && data.status === 'failed') {
                    setPaymentStatus('failed');
                    return;
                }
                if (attemptsLeft > 0) {
                    timerId = setTimeout(() => poll(attemptsLeft - 1), 1500);
                } else {
                    setPaymentStatus('pending');
                }
            } catch {
                if (cancelled) return;
                if (attemptsLeft > 0) {
                    timerId = setTimeout(() => poll(attemptsLeft - 1), 1500);
                } else {
                    setPaymentStatus('pending');
                }
            }
        };
        poll(5);
        return () => { cancelled = true; if (timerId) clearTimeout(timerId); };
    }, [returnOrderId, refreshUser]);

    const hasSubscription = user?.subscriptionEndDate && new Date(user.subscriptionEndDate) >= new Date();
    const coins = user?.coins ?? 0;

    React.useEffect(() => {
        const token = getToken();
        if (!token) {
            navigate('/login');
            return;
        }
        if (user && user.role !== 'admin' && user.role !== 'superadmin') {
            navigate('/dashboard');
        }
    }, [navigate, user]);

    const handleBuyCoins = async () => {
        setBuyError('');
        const amount = Number(buyAmount);
        if (!Number.isFinite(amount) || amount < MIN_AMOUNT || amount > MAX_AMOUNT) {
            setBuyError(`Enter an amount between ₹${MIN_AMOUNT} and ₹${MAX_AMOUNT.toLocaleString('en-IN')}.`);
            return;
        }
        setBuyLoading(true);
        try {
            const res = await request(`${API_PATH}/payments/create-order`, {
                method: 'POST',
                headers: { 'Content-Type': 'application/json' },
                body: JSON.stringify({ amount }),
            });
            const data = await res.json().catch(() => ({}));
            if (!res.ok) {
                setBuyError(data.error || 'Could not start payment. Try again.');
                setBuyLoading(false);
                return;
            }
            if (!data.payment_url) {
                setBuyError('Could not start payment. Try again.');
                setBuyLoading(false);
                return;
            }
            window.location.href = data.payment_url;
        } catch {
            setBuyError('Network error. Try again.');
            setBuyLoading(false);
        }
    };

    const handleBuyPlatinum = async () => {
        setPlatinumError('');
        if (!selectedTier) {
            setPlatinumError('Choose a subscription duration first.');
            return;
        }
        setPlatinumLoading(true);
        try {
            const res = await request(`${API_PATH}/payments/create-order`, {
                method: 'POST',
                headers: { 'Content-Type': 'application/json' },
                body: JSON.stringify({ product: 'platinum', months: selectedTier.months }),
            });
            const data = await res.json().catch(() => ({}));
            if (!res.ok || !data.payment_url) {
                setPlatinumError(data.error || 'Could not start payment. Try again.');
                setPlatinumLoading(false);
                return;
            }
            window.location.href = data.payment_url;
        } catch {
            setPlatinumError('Network error. Try again.');
            setPlatinumLoading(false);
        }
    };

    const handleRedeemCoupon = async (e) => {
        e.preventDefault();
        setRedeemMessage({ type: '', text: '' });
        const code = couponCode.trim();
        if (!code) {
            setRedeemMessage({ type: 'error', text: 'Enter a coupon code.' });
            return;
        }
        setRedeemLoading(true);
        try {
            const res = await request(`${API_PATH}/coupons/redeem`, {
                method: 'POST',
                headers: { 'Content-Type': 'application/json' },
                body: JSON.stringify({ code }),
            });
            const data = await res.json().catch(() => ({}));
            if (res.ok) {
                setRedeemMessage({ type: 'success', text: 'Coupon redeemed! Your balance has been updated.' });
                setCouponCode('');
                refreshUser();
                const fire = (opts) => confetti({ origin: { y: 0.75 }, ...opts });
                fire({ particleCount: 80, spread: 55 });
                setTimeout(() => fire({ particleCount: 60, spread: 100, startVelocity: 35 }), 150);
                setTimeout(() => fire({ particleCount: 50, spread: 120, origin: { x: 0.2, y: 0.8 } }), 250);
                setTimeout(() => fire({ particleCount: 50, spread: 120, origin: { x: 0.8, y: 0.8 } }), 250);
            } else {
                setRedeemMessage({ type: 'error', text: data.error || 'Invalid or already used coupon.' });
            }
        } catch {
            setRedeemMessage({ type: 'error', text: 'Network error. Try again.' });
        } finally {
            setRedeemLoading(false);
        }
    };

    if (!user) return null;
    if (user.role !== 'admin' && user.role !== 'superadmin') return null;

    return (
        <div className={`${styles.page} ${styles.creditsPage}`}>
            <PageHeroHeader
                icon={Coins}
                eyebrow="Account"
                title="Credits & Coupon"
                subtitle="Balance, redeem codes, and transaction history."
                actions={<span className={`trust-pill ${styles.subStatusPill}`}><span className={`trust-pill-dot ${hasSubscription ? '' : styles.trustDotMuted}`} aria-hidden />{hasSubscription ? 'Active subscription' : 'No active subscription'}</span>}
            />

            {paymentStatus === 'completed' && (
                <div className={styles.alertSuccess} role="alert">
                    {paymentProduct === 'platinum'
                        ? `Payment successful — Platinum is active${completedMonths ? ` for ${formatMonths(completedMonths)}` : ''}!`
                        : 'Payment successful — your coins have been added!'}
                </div>
            )}
            {paymentStatus === 'pending' && (
                <div className={styles.alertWarning} role="alert">Payment received — this is being processed, check back in a minute.</div>
            )}
            {paymentStatus === 'failed' && (
                <div className={styles.alertError} role="alert">Payment failed or was cancelled. Nothing was charged.</div>
            )}
            {paymentStatus === 'unknown' && (
                <div className={styles.alertWarning} role="alert">We couldn't find that payment. Check your coin history, or contact support if you were charged.</div>
            )}

            <div className={styles.grid}>
                <section className={`${styles.card} ${styles.creditsCard}`} aria-labelledby="credits-heading">
                    <div className={`${styles.cardHead} ${styles.creditsSectionHead}`}>
                        <span className={styles.cardIcon} aria-hidden>
                            <Coins size={20} />
                        </span>
                        <div>
                            <h2 id="credits-heading" className={styles.cardTitle}>Current status</h2>
                            <p className={styles.cardDesc}>Your available balance and subscription status</p>
                        </div>
                    </div>
                    <div className={styles.statsRow}>
                        <div className={`${styles.statCard} ${styles.statCardHero}`}>
                            <Coins size={22} className={styles.statIcon} aria-hidden />
                            <div>
                                <span className={styles.statValue}>{coins}</span>
                                <span className={styles.statLabel}>Coins</span>
                                <span className={styles.statHint}>Used for polls &amp; event extension</span>
                            </div>
                            {redeemMessage.type === 'success' && (
                                <span className={`sticker-badge ${styles.stickerFlourish}`}>Redeemed!</span>
                            )}
                        </div>
                        {user.subscriptionEndDate ? (
                            <div className={styles.statCard}>
                                <Calendar size={22} className={styles.statIcon} aria-hidden />
                                <div>
                                    <span className={styles.statValue}>{hasSubscription ? 'Active' : 'Ended'}</span>
                                    <span className={styles.statLabel}>{formatDate(user.subscriptionEndDate)}</span>
                                    <span className={styles.statHint}>{hasSubscription ? 'Unlimited polls' : 'Subscription ended'}</span>
                                </div>
                            </div>
                        ) : (
                            <div className={`${styles.statCard} ${styles.statCardMuted}`}>
                                <Calendar size={22} className={styles.statIcon} aria-hidden />
                                <div>
                                    <span className={styles.statValue}>—</span>
                                    <span className={styles.statLabel}>No subscription</span>
                                </div>
                            </div>
                        )}
                    </div>
                    <div className={`${styles.creditsSection} ${styles.purchaseSection}`}>
                        <div className={styles.sectionHead}>
                            <div>
                                <h3 className={styles.sectionTitle}>Purchase credits</h3>
                                <p className={styles.sectionDescription}>Add coins to use for polls and event extensions.</p>
                            </div>
                        </div>
                        <form className={styles.sectionForm} onSubmit={(e) => { e.preventDefault(); handleBuyCoins(); }}>
                            <label htmlFor="buy-amount" className={styles.label}>Choose an amount</label>
                            <div className={styles.amountChips} role="group" aria-label="Quick amounts">
                                {AMOUNT_PRESETS.map((preset) => (
                                    <button
                                        key={preset}
                                        type="button"
                                        className={`${styles.amountChip} ${Number(buyAmount) === preset ? styles.amountChipActive : ''}`}
                                        onClick={() => { setBuyAmount(String(preset)); setBuyError(''); }}
                                        disabled={buyLoading}
                                        aria-pressed={Number(buyAmount) === preset}
                                    >
                                        ₹{preset}
                                    </button>
                                ))}
                            </div>
                            <div className={styles.couponInputRow}>
                                <input
                                    id="buy-amount"
                                    type="number"
                                    min="10"
                                    max="100000"
                                    step="1"
                                    className={`${styles.input} ${styles.pillInput}`}
                                    value={buyAmount}
                                    onChange={(e) => {
                                        const nextValue = e.target.value;
                                        if (nextValue === '' || /^\d*$/.test(nextValue)) {
                                            setBuyAmount(nextValue);
                                            setBuyError('');
                                        }
                                    }}
                                    onKeyDown={(e) => {
                                        if (['-', '+', 'e', 'E'].includes(e.key)) e.preventDefault();
                                    }}
                                    placeholder="Custom amount (₹)"
                                    inputMode="numeric"
                                    disabled={buyLoading}
                                />
                                <Button type="submit" variant="primary" size="sm" className={styles.pillBtn} disabled={buyLoading || !previewCoins}>
                                    {buyLoading ? 'Redirecting…' : 'Pay with UPI'}
                                </Button>
                            </div>
                            {previewCoins != null && (
                                <p className={styles.coinPreview} aria-live="polite">
                                    You&apos;ll get <strong>{previewCoins} coins</strong>
                                    {hasBonus && <span className={styles.bonusBadge}>+{bonusPercentLabel}% bonus</span>}
                                </p>
                            )}
                            {buyError && <div className={styles.alertError} role="alert">{buyError}</div>}
                        </form>
                    </div>
                    <div className={`${styles.creditsSection} ${styles.platinumSection}`}>
                        <label className={styles.label}>
                            <Crown size={15} style={{ verticalAlign: '-2px', marginRight: 4 }} aria-hidden />
                            Get Platinum
                        </label>
                        <p className={styles.coinPreview}>
                            Unlimited events, polls &amp; participants — no coin limits.
                            {hasSubscription && <span className={styles.bonusBadge}>Adds to your current subscription</span>}
                        </p>
                        <div className={styles.amountChips} role="group" aria-label="Subscription duration">
                            {platinumTiers.map((tier) => {
                                const discountPercent = baseMonthlyRate > 0
                                    ? Math.round((1 - tier.priceRupees / (baseMonthlyRate * tier.months)) * 100)
                                    : 0;
                                return (
                                    <button
                                        key={tier.months}
                                        type="button"
                                        className={`${styles.amountChip} ${selectedMonths === tier.months ? styles.amountChipActive : ''}`}
                                        onClick={() => setSelectedMonths(tier.months)}
                                        disabled={platinumLoading}
                                        aria-pressed={selectedMonths === tier.months}
                                    >
                                        {formatMonths(tier.months)}
                                        {discountPercent > 0 ? ` · −${discountPercent}%` : ''}
                                    </button>
                                );
                            })}
                        </div>
                        {selectedTier && (
                            <p className={styles.coinPreview}>
                                <strong>₹{selectedTier.priceRupees.toLocaleString('en-IN')}</strong> for {formatMonths(selectedTier.months)}
                            </p>
                        )}
                        <Button
                            type="button"
                            variant="primary"
                            size="sm"
                            className={styles.pillBtn}
                            disabled={platinumLoading || !selectedTier}
                            onClick={handleBuyPlatinum}
                        >
                            {platinumLoading ? 'Redirecting…' : selectedTier ? `Pay ₹${selectedTier.priceRupees.toLocaleString('en-IN')} with UPI` : 'Select a plan'}
                        </Button>
                        {platinumError && <div className={styles.alertError} role="alert">{platinumError}</div>}
                    </div>
                    <div className={`${styles.creditsSection} ${styles.redeemSection}`}>
                        <div className={styles.sectionHead}>
                            <div>
                                <h3 className={styles.sectionTitle}>Redeem coupon</h3>
                                <p className={styles.sectionDescription}>Apply a code to add coins or subscription time.</p>
                            </div>
                        </div>
                        <form onSubmit={handleRedeemCoupon} className={styles.sectionForm}>
                            <div className={styles.couponInputRow}>
                                <input
                                    id="coupon-code"
                                    type="text"
                                    className={`${styles.input} ${styles.pillInput}`}
                                    value={couponCode}
                                    onChange={(e) => setCouponCode(e.target.value.toUpperCase())}
                                    placeholder="Enter coupon code"
                                    disabled={redeemLoading}
                                    autoComplete="off"
                                />
                                <Button type="submit" variant="primary" size="sm" disabled={redeemLoading} className={styles.pillBtn}>
                                    {redeemLoading ? 'Applying…' : 'Apply coupon'}
                                </Button>
                            </div>
                            {redeemMessage.text && (
                                <div className={redeemMessage.type === 'success' ? styles.alertSuccess : styles.alertError} role="alert">
                                    {redeemMessage.text}
                                </div>
                            )}
                        </form>
                        <div className={styles.historyLinks}>
                            <button type="button" className={styles.historyLink} onClick={() => setCoinHistoryOpen(true)}>
                                <History size={16} aria-hidden /> Coin history
                            </button>
                            <button type="button" className={styles.historyLink} onClick={() => setCouponHistoryOpen(true)}>
                                <Ticket size={16} aria-hidden /> Coupon history
                            </button>
                        </div>
                    </div>
                    <CoinHistoryModal isOpen={coinHistoryOpen} onClose={() => setCoinHistoryOpen(false)} />
                    <CouponHistoryModal isOpen={couponHistoryOpen} onClose={() => setCouponHistoryOpen(false)} />
                </section>
            </div>
        </div>
    );
};

export default CreditsCouponPage;
